import React, { useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaBuilding,
  FaBell,
  FaChartBar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLeaf,
  FaMapMarkerAlt,
  FaMicrochip,
  FaSync,
  FaTools,
  FaUsers,
} from "react-icons/fa";

const API = process.env.REACT_APP_API_URL || "/api";
const SOCKET_URL = window.location.origin;

const STATUS_COLORS = {
  Online: "#16A34A",
  Maintenance: "#F59E0B",
  Offline: "#EF4444",
};

const ALERT_COLORS = {
  critical: "#DC2626",
  high: "#F97316",
  medium: "#F59E0B",
  low: "#2563EB",
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (value, digits = 0) =>
  toNumber(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });

const normalizeTrend = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.data;
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    date: row.date ? new Date(row.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "-",
    co2: toNumber(row.co2),
  }));
};

const statusLabel = (device = {}) => {
  const raw = String(device.status || "").toLowerCase();
  if (raw === "online" || raw === "active") return "Online";
  if (raw === "maintenance") return "Maintenance";
  return "Offline";
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-xl shadow-slate-900/10">
      <p className="mb-1 font-medium text-gray-400">{label}</p>
      {payload.map((item) => (
        <p key={item.name} className="font-semibold text-gray-800">
          {item.name}: <span style={{ color: item.color }}>{item.value}</span>
        </p>
      ))}
    </div>
  );
};

const LoadingScreen = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50">
    <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-gray-200 border-t-blue-600" />
    <p className="text-sm font-medium text-gray-400">Loading dashboard...</p>
  </div>
);

const EmptyPanel = ({ message }) => (
  <div className="flex h-full min-h-[220px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-sm font-medium text-gray-400">
    {message}
  </div>
);

const StatCard = ({ title, value, unit, icon: Icon, accent, note }) => (
  <div className="cb-panel rounded-lg p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{title}</p>
        <div className="mt-2 flex items-end gap-1.5">
          <span className="text-3xl font-bold tracking-tight text-gray-950">{value}</span>
          {unit && <span className="mb-1 text-xs font-semibold text-gray-400">{unit}</span>}
        </div>
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent.bg}`}>
        <Icon className={accent.text} size={16} />
      </div>
    </div>
    {note && <p className="mt-3 text-xs font-medium text-gray-400">{note}</p>}
  </div>
);

const SectionHeader = ({ title, meta }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    {meta && (
      <span className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-500">
        {meta}
      </span>
    )}
  </div>
);

const HealthStrip = ({ online, maintenance, offline, total }) => {
  const parts = [
    { label: "Online", value: online, color: "bg-green-500" },
    { label: "Maintenance", value: maintenance, color: "bg-amber-500" },
    { label: "Offline", value: offline, color: "bg-red-500" },
  ];

  return (
    <div className="cb-panel rounded-lg p-5">
      <SectionHeader title="Fleet Health" meta={`${total} devices`} />
      <div className="flex h-3 overflow-hidden rounded-full bg-gray-100">
        {parts.map((part) => (
          <div
            key={part.label}
            className={part.color}
            style={{ width: `${total ? (part.value / total) * 100 : 0}%` }}
          />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {parts.map((part) => (
          <div key={part.label} className="rounded-md border border-gray-100 bg-gray-50 p-3">
            <p className="text-[11px] font-semibold text-gray-400">{part.label}</p>
            <p className="mt-1 text-xl font-bold text-gray-900">{part.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AlertSummary = ({ summary = {} }) => {
  const data = ["critical", "high", "medium", "low"].map((key) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    key,
    value: toNumber(summary[key]),
  }));

  return (
    <div className="cb-panel rounded-lg p-5">
      <SectionHeader title="Alert Severity" meta={`${fmt(summary.total)} total`} />
      <div className="grid grid-cols-2 gap-2">
        {data.map((item) => (
          <div key={item.key} className="rounded-md border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-gray-500">{item.name}</span>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ALERT_COLORS[item.key] }} />
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-950">{item.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {(summary.latest || []).slice(0, 3).map((alert) => (
          <div key={alert.id} className="rounded-md bg-gray-50 px-3 py-2">
            <p className="truncate text-xs font-semibold text-gray-800">{alert.message || alert.type || "Alert"}</p>
            <p className="mt-0.5 text-[11px] font-medium text-gray-400">{alert.device_uid || "Device"} · {alert.severity || "medium"}</p>
          </div>
        ))}
        {!(summary.latest || []).length && (
          <p className="rounded-md bg-gray-50 px-3 py-3 text-xs font-medium text-gray-400">No recent alerts.</p>
        )}
      </div>
    </div>
  );
};

const AttentionPanel = ({ items = [] }) => (
  <div className="cb-panel rounded-lg p-5">
    <SectionHeader title="Needs Attention" meta={`${items.length} items`} />
    {items.length ? (
      <div className="space-y-2">
        {items.slice(0, 8).map((item, index) => (
          <div key={`${item.device_uid}-${item.type}-${index}`} className="flex items-start gap-3 rounded-md border border-gray-100 bg-gray-50 p-3">
            <span
              className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: ALERT_COLORS[item.severity] || "#64748B" }}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{item.device_uid}</p>
              <p className="text-xs font-medium text-gray-500">{item.message}</p>
              <p className="mt-0.5 text-[11px] font-medium text-gray-400">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <EmptyPanel message="No devices need attention right now." />
    )}
  </div>
);

const SensorHealthPanel = ({ rows = [] }) => (
  <div className="cb-panel rounded-lg p-5">
    <SectionHeader title="Sensor Coverage" meta="Lowest first" />
    {rows.length ? (
      <div className="space-y-2">
        {rows.slice(0, 10).map((row) => {
          const color = row.percent >= 90 ? "#16A34A" : row.percent >= 60 ? "#F59E0B" : "#EF4444";
          return (
            <div key={row.device_uid} className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-gray-900">{row.device_uid}</p>
                  <p className="truncate text-[11px] font-medium text-gray-400">{row.company_name || row.product_name || "Unassigned"}</p>
                </div>
                <span className="text-xs font-bold text-gray-800">{row.percent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full rounded-full" style={{ width: `${row.percent}%`, backgroundColor: color }} />
              </div>
              <p className="mt-1.5 text-[11px] font-medium text-gray-400">{row.reporting}/{row.configured} sensors reporting</p>
            </div>
          );
        })}
      </div>
    ) : (
      <EmptyPanel message="No sensor configuration found." />
    )}
  </div>
);

const BreakdownList = ({ title, meta, rows = [], valueLabel = "devices" }) => (
  <div className="cb-panel rounded-lg p-5">
    <SectionHeader title={title} meta={meta} />
    {rows.length ? (
      <div className="space-y-2">
        {rows.slice(0, 8).map((row) => {
          const onlineRate = row.total ? Math.round((row.online / row.total) * 100) : 0;
          return (
            <div key={row.name} className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{row.name}</p>
                  <p className="text-xs font-medium text-gray-400">{row.total} {valueLabel} · {fmt(row.co2, 2)} CO2</p>
                </div>
                <span className="rounded bg-white px-2 py-1 text-xs font-bold text-green-700">{onlineRate}% online</span>
              </div>
              <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-gray-200">
                <div className="bg-green-500" style={{ width: `${row.total ? (row.online / row.total) * 100 : 0}%` }} />
                <div className="bg-amber-500" style={{ width: `${row.total ? (row.maintenance / row.total) * 100 : 0}%` }} />
                <div className="bg-red-500" style={{ width: `${row.total ? (row.offline / row.total) * 100 : 0}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <EmptyPanel message="No breakdown data available." />
    )}
  </div>
);

const DeviceRow = ({ device }) => {
  const status = statusLabel(device);
  const lastSeen = device.last_seen
    ? new Date(device.last_seen).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "No recent data";

  return (
    <div className="grid grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr] items-center gap-3 border-t border-gray-100 px-4 py-3 text-sm first:border-t-0">
      <div className="min-w-0">
        <p className="truncate font-semibold text-gray-900">{device.device_uid || "Device"}</p>
        <p className="truncate text-xs font-medium text-gray-400">{device.company_name || device.product_name || "Unassigned"}</p>
      </div>
      <span
        className="w-fit rounded-md px-2 py-1 text-xs font-semibold"
        style={{ color: STATUS_COLORS[status], backgroundColor: `${STATUS_COLORS[status]}14` }}
      >
        {status}
      </span>
      <p className="text-xs font-medium text-gray-500">{fmt(device.co2, 2)} CO2</p>
      <p className="text-xs font-medium text-gray-400">{lastSeen}</p>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const token = localStorage.getItem("token");

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data || {});
      setLastUpdate(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch {
      setStats({});
    }
  };

  const fetchTrend = async () => {
    try {
      const res = await fetch(`${API}/dashboard/trend`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTrendData(normalizeTrend(data));
    } catch {
      setTrendData([]);
    }
  };

  useEffect(() => {
    if (!token) return;

    (async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTrend()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;

    const socket = io(SOCKET_URL, { transports: ["websocket"] });
    socket.on("connect", () => setIsLive(true));
    socket.on("disconnect", () => setIsLive(false));
    socket.on("deviceData", () => {
      setLastUpdate(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      fetchDashboard();
      fetchTrend();
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const dashboard = stats || {};
  const devices = useMemo(() => Array.isArray(dashboard.devices) ? dashboard.devices : [], [dashboard.devices]);

  const totalDevices = toNumber(dashboard.totalDevices);
  const online = toNumber(dashboard.activeDevices);
  const maintenance = toNumber(dashboard.maintenanceDevices);
  const offline = toNumber(dashboard.offlineDevices);
  const onlineRate = totalDevices ? Math.round((online / totalDevices) * 100) : 0;
  const avgCo2 = toNumber(dashboard.avgCO2);
  const totalCo2 = toNumber(dashboard.totalCO2);
  const alertSummary = dashboard.alertSummary || {};
  const attentionItems = Array.isArray(dashboard.attentionItems) ? dashboard.attentionItems : [];
  const companyBreakdown = Array.isArray(dashboard.companyBreakdown) ? dashboard.companyBreakdown : [];
  const productBreakdown = Array.isArray(dashboard.productBreakdown) ? dashboard.productBreakdown : [];
  const sensorHealth = Array.isArray(dashboard.sensorHealth) ? dashboard.sensorHealth : [];
  const locationRows = companyBreakdown.filter((row) => row.location);

  const statusData = [
    { name: "Online", value: online },
    { name: "Maintenance", value: maintenance },
    { name: "Offline", value: offline },
  ].filter((item) => item.value > 0);

  const topDevices = [...devices]
    .sort((a, b) => toNumber(b.co2) - toNumber(a.co2))
    .slice(0, 8)
    .map((device) => ({
      name: device.device_uid || `Device ${device.id}`,
      co2: toNumber(device.co2),
    }));

  if (loading || !stats) return <LoadingScreen />;

  return (
    <div className="px-1 py-1">
      <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase text-emerald-700">IoT Platform</p>
          <h1 className="mt-1 text-3xl font-black text-slate-950">Operations Dashboard</h1>
          <p className="mt-1 text-sm font-medium text-gray-500">
            Fleet visibility, live status, CO2 performance, and account coverage.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {lastUpdate && <span className="text-xs font-medium text-gray-400">Updated {lastUpdate}</span>}
          <button
            onClick={() => { fetchDashboard(); fetchTrend(); }}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white"
          >
            <FaSync size={12} className="text-gray-400" />
            Refresh
          </button>
          <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${isLive ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-600"}`}>
            <span className={`h-2 w-2 rounded-full ${isLive ? "animate-pulse bg-green-500" : "bg-red-400"}`} />
            {isLive ? "Live" : "Offline"}
          </div>
        </div>
      </header>
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Devices" value={fmt(totalDevices)} icon={FaMicrochip} accent={{ bg: "bg-blue-50", text: "text-blue-600" }} note={`${onlineRate}% currently online`} />
        <StatCard title="Companies" value={fmt(dashboard.totalCompanies)} icon={FaBuilding} accent={{ bg: "bg-violet-50", text: "text-violet-600" }} note="Managed customer accounts" />
        <StatCard title="Users" value={fmt(dashboard.totalUsers)} icon={FaUsers} accent={{ bg: "bg-sky-50", text: "text-sky-600" }} note="Platform access holders" />
        <StatCard title="Total CO2" value={fmt(totalCo2, 2)} unit="ppm" icon={FaLeaf} accent={{ bg: "bg-emerald-50", text: "text-emerald-600" }} note={`${fmt(avgCo2, 2)} ppm average per device`} />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Open Alerts" value={fmt(alertSummary.total)} icon={FaBell} accent={{ bg: "bg-red-50", text: "text-red-600" }} note={`${fmt(alertSummary.critical)} critical - ${fmt(alertSummary.high)} high`} />
        <StatCard title="Attention Items" value={fmt(attentionItems.length)} icon={FaExclamationTriangle} accent={{ bg: "bg-amber-50", text: "text-amber-600" }} note="Offline, coverage, and CO2 risk" />
        <StatCard title="Product Types" value={fmt(productBreakdown.length)} icon={FaChartBar} accent={{ bg: "bg-indigo-50", text: "text-indigo-600" }} note="Configured device categories" />
        <StatCard title="Known Locations" value={fmt(locationRows.length)} icon={FaMapMarkerAlt} accent={{ bg: "bg-teal-50", text: "text-teal-600" }} note="Companies with saved location" />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_360px]">
        <div className="cb-panel rounded-lg p-5">
          <SectionHeader title="CO2 Trend" meta={`${trendData.length} daily points`} />
          {trendData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="co2Trend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16A34A" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#16A34A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="co2" name="CO2" stroke="#16A34A" strokeWidth={2.5} fill="url(#co2Trend)" activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPanel message="No historical CO2 data available yet." />
          )}
        </div>

        <div className="cb-panel rounded-lg p-5">
          <SectionHeader title="Device Status" meta="Live split" />
          {statusData.length ? (
            <>
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={88} paddingAngle={3} strokeWidth={0}>
                    {statusData.map((entry) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {statusData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[entry.name] }} />
                      {entry.name}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyPanel message="No devices registered yet." />
          )}
        </div>
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[420px_1fr]">
        <HealthStrip online={online} maintenance={maintenance} offline={offline} total={totalDevices} />

        <div className="cb-panel rounded-lg p-5">
          <SectionHeader title="Highest CO2 Devices" meta="Top 8" />
          {topDevices.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topDevices} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="co2" name="CO2" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPanel message="No device readings available yet." />
          )}
        </div>
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[420px_1fr_420px]">
        <AlertSummary summary={alertSummary} />
        <AttentionPanel items={attentionItems} />
        <SensorHealthPanel rows={sensorHealth} />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <BreakdownList title="Company Performance" meta="Top companies" rows={companyBreakdown} valueLabel="devices" />
        <BreakdownList title="Product Mix" meta="Configured products" rows={productBreakdown} valueLabel="units" />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_420px]">
        <div className="cb-panel rounded-lg p-5">
          <SectionHeader title="Company CO2 Performance" meta={`${companyBreakdown.length} groups`} />
          {companyBreakdown.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={companyBreakdown.slice(0, 8)} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="co2" name="CO2" fill="#0F766E" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyPanel message="No company performance data available." />
          )}
        </div>

        <div className="cb-panel rounded-lg p-5">
          <SectionHeader title="Location Coverage" meta={`${locationRows.length} saved`} />
          {locationRows.length ? (
            <div className="space-y-2">
              {locationRows.slice(0, 8).map((row) => (
                <div key={`${row.name}-${row.location}`} className="flex items-start gap-3 rounded-md border border-gray-100 bg-gray-50 p-3">
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-50">
                    <FaMapMarkerAlt className="text-teal-600" size={13} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">{row.name}</p>
                    <p className="truncate text-xs font-medium text-gray-500">{row.location}</p>
                    <p className="mt-0.5 text-[11px] font-medium text-gray-400">{row.total} devices · {row.online} online</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyPanel message="No company locations saved yet." />
          )}
        </div>
      </section>

      <section className="cb-panel mt-3 rounded-lg">
        <div className="flex items-center justify-between px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Recent Fleet Activity</h2>
            <p className="mt-0.5 text-xs font-medium text-gray-400">Latest device health and CO2 readings</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
            <FaCheckCircle className="text-green-500" />
            {online} online
            <FaTools className="ml-2 text-amber-500" />
            {maintenance} maintenance
            <FaExclamationTriangle className="ml-2 text-red-500" />
            {offline} offline
          </div>
        </div>

        {devices.length ? (
          <div className="divide-y divide-gray-100">
            {devices.slice(0, 10).map((device) => (
              <DeviceRow key={device.id || device.device_uid} device={device} />
            ))}
          </div>
        ) : (
          <div className="px-5 pb-5">
            <EmptyPanel message="No devices registered yet." />
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDashboard;
